import React from "react";
import { useSettings } from "@/context/SettingsContext";
import { IMAGES } from "@/constants/images";
import PageHero from "@/components/public/PageHero";
import SectionHeading from "@/components/public/SectionHeading";
import CTASection from "@/components/public/CTASection";
import Reveal from "@/components/public/Reveal";
import DownloadButton from "@/components/public/DownloadButton";
import { Boxes, Warehouse, ShieldCheck, Handshake, Ship, Users } from "lucide-react";

const PILLARS = [
  { icon: Boxes, title: "Coffee Sourcing", desc: "Sourcing Indonesian Arabica from selected producing areas and partners." },
  { icon: Warehouse, title: "Warehouse Operations", desc: "Controlled storage and organized handling of green coffee inventory." },
  { icon: ShieldCheck, title: "Quality", desc: "Inspection and preparation to agreed, transparent specifications." },
  { icon: Handshake, title: "Trading", desc: "Clear commercial terms and honest communication with buyers." },
  { icon: Ship, title: "Export Preparation", desc: "Packaging, documentation and freight coordination for shipment." },
  { icon: Users, title: "Buyer Relationships", desc: "Long-term relationships built on reliability and transparency." },
];

export default function About() {
  const { settings } = useSettings();
  return (
    <div>
      <PageHero eyebrow="Who We Are" title="About Warehouse Coffee" sub={settings.description} image={IMAGES.greenBeansPile} />
      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="aspect-[4/3] overflow-hidden rounded-2xl"><img src={IMAGES.beansScoop} alt="Green coffee" className="h-full w-full object-cover" /></div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-brand">Established {settings.established_year || "2020"}</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-forest-dark sm:text-4xl">A Real Indonesian Green Coffee Business</h2>
            <p className="mt-4 font-body text-base leading-relaxed text-espresso/75">
              Warehouse Coffee is an Indonesian green coffee business focused on sourcing, preparing and supplying Arabica coffee for domestic and international buyers. We built our identity around a physical supply and trading operation — with actual inventory and an organized export process.
            </p>
            <p className="mt-4 font-body text-base leading-relaxed text-espresso/75">
              Our credibility comes from transparency: real specifications, real inventory and real evidence of activity — never exaggerated claims.
            </p>
            {(settings.company_profile_pdf || settings.catalog_pdf) && (
              <div className="mt-8 flex flex-wrap gap-3">
                <DownloadButton url={settings.company_profile_pdf} label="Company Profile" filename="warehouse-coffee-profile.pdf" testId="about-profile-download" />
                <DownloadButton url={settings.catalog_pdf} label="Green Coffee Catalog" filename="warehouse-coffee-catalog.pdf" variant="light" testId="about-catalog-download" />
              </div>
            )}
          </Reveal>
        </div>
      </section>
      <section className="bg-beige py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <SectionHeading eyebrow="What We Do" title="Our Focus" />
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 90}>
                <div className="h-full rounded-xl border border-forest/10 bg-white p-7">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-forest text-offwhite"><p.icon className="h-6 w-6" /></div>
                  <h3 className="font-display text-lg font-bold text-forest-dark">{p.title}</h3>
                  <p className="mt-2 font-body text-sm leading-relaxed text-espresso/65">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
